import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { listItems, listSuppliers } from '../../api/masterData';
import { listGoodsReceipts, listPurchaseOrders, listPurchaseRequests } from '../../api/purchasing';
import { listStockBalances, listStockLedger } from '../../api/inventory';
import { usePermissions } from '../../context/PermissionContext';
import { useBranch } from '../../context/BranchContext';
import { PrDetailDrawer } from '../../components/PrDetailDrawer';
import {
  ClickableRow,
  DashboardSkeleton,
  ErrorNote,
  IconCircle,
  KPICard,
  SectionCard,
  Sparkline,
  StatusBadge,
  tableStyle,
  tdStyle,
  thStyle,
} from '../../components/common';
import { IconBox, IconClipboardCheck, IconFileText, IconTruck, IconUsers } from '../../components/icons';
import type { GoodsReceipt, Item, PurchaseOrder, PurchaseRequest, StockBalance, StockLedgerEntry, Supplier } from '../../types/domain';

/**
 * Real-data executive dashboard — the default view for TENANT_OWNER (and
 * the closest analog to the "Owner / Executive Command Center" persona in
 * the reference screenshots that this vertical slice can back with real
 * data). Every number is a live count from the same PostgREST/RPC calls
 * the vertical-slice pages themselves use, RLS-scoped to the tenant. A
 * section only renders when the user has the matching `<resource>.read`
 * permission. No platform/SaaS or sales figure is fabricated — those have
 * no authoritative source in this vertical slice yet.
 */
export default function ExecutiveDashboard() {
  const navigate = useNavigate();
  const { hasPermission } = usePermissions();
  const { activeBranchId, activeBranchWarehouseIds } = useBranch();
  const [items, setItems] = useState<Item[] | null>(null);
  const [suppliers, setSuppliers] = useState<Supplier[] | null>(null);
  const [prs, setPrs] = useState<PurchaseRequest[] | null>(null);
  const [pos, setPos] = useState<PurchaseOrder[] | null>(null);
  const [grs, setGrs] = useState<GoodsReceipt[] | null>(null);
  const [balances, setBalances] = useState<StockBalance[] | null>(null);
  const [recentMovements, setRecentMovements] = useState<StockLedgerEntry[] | null>(null);
  const [ledgerTrend, setLedgerTrend] = useState<number[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedPr, setSelectedPr] = useState<PurchaseRequest | null>(null);

  const canReadItem = hasPermission('item', 'read');
  const canReadSupplier = hasPermission('supplier', 'read');
  const canReadPr = hasPermission('purchase_request', 'read');
  const canReadPo = hasPermission('purchase_order', 'read');
  const canReadGr = hasPermission('goods_receipt', 'read');
  const canReadBalance = hasPermission('stock_balance', 'read');
  const canReadLedger = hasPermission('stock_ledger', 'read');

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    Promise.all([
      canReadItem ? listItems() : Promise.resolve(null),
      canReadSupplier ? listSuppliers() : Promise.resolve(null),
      canReadPr ? listPurchaseRequests(activeBranchId ?? undefined) : Promise.resolve(null),
      canReadPo ? listPurchaseOrders(activeBranchId ?? undefined) : Promise.resolve(null),
      canReadGr ? listGoodsReceipts(activeBranchId ?? undefined) : Promise.resolve(null),
      canReadBalance ? listStockBalances(undefined, activeBranchId ? activeBranchWarehouseIds : undefined) : Promise.resolve(null),
      canReadLedger ? listStockLedger(undefined, activeBranchId ?? undefined) : Promise.resolve(null),
    ])
      .then(([itemRows, supplierRows, prRows, poRows, grRows, balanceRows, ledgerRows]) => {
        if (cancelled) return;
        setItems(itemRows);
        setSuppliers(supplierRows);
        setPrs(prRows);
        setPos(poRows);
        setGrs(grRows);
        setBalances(balanceRows);
        setRecentMovements(ledgerRows ? ledgerRows.slice(0, 6) : null);
        if (ledgerRows) {
          const byDay: Record<string, number> = {};
          for (const row of ledgerRows) {
            const day = row.occurred_at.slice(0, 10);
            byDay[day] = (byDay[day] ?? 0) + row.qty_delta;
          }
          const days = Object.keys(byDay).sort().slice(-14);
          setLedgerTrend(days.length >= 2 ? days.map((d) => byDay[d]) : null);
        }
      })
      .catch((e) => !cancelled && setError(e instanceof Error ? e.message : String(e)))
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canReadItem, canReadSupplier, canReadPr, canReadPo, canReadGr, canReadBalance, canReadLedger, activeBranchId, activeBranchWarehouseIds.join(',')]);

  const countByStatus = <T extends { status: string }>(rows: T[] | null, status: string) => (rows ? rows.filter((r) => r.status === status).length : null);

  if (loading) return <DashboardSkeleton />;
  if (error) return <ErrorNote text={error} />;

  const noAccess = !canReadItem && !canReadSupplier && !canReadPr && !canReadPo && !canReadGr && !canReadBalance;

  return (
    <div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--alex-card-gap)', marginBottom: 24 }}>
        {canReadItem && (
          <KPICard label="Items" value={items?.length ?? '—'} hint="Master item aktif & non-aktif" icon={<IconBox size={19} />} tone="primary" linkTo="/app/items" />
        )}
        {canReadSupplier && (
          <KPICard label="Suppliers" value={suppliers?.length ?? '—'} hint="Master pemasok" icon={<IconUsers size={19} />} tone="info" linkTo="/app/suppliers" />
        )}
        {canReadPr && (
          <KPICard
            label="PR menunggu approval"
            value={countByStatus(prs, 'SUBMITTED') ?? '—'}
            hint={`${prs?.length ?? 0} total PR`}
            icon={<IconFileText size={19} />}
            tone="warning"
            linkTo="/app/purchase-requests"
          />
        )}
        {canReadPo && (
          <KPICard
            label="PO berjalan"
            value={(countByStatus(pos, 'ISSUED') ?? 0) + (countByStatus(pos, 'PARTIALLY_RECEIVED') ?? 0)}
            hint={`${pos?.length ?? 0} total PO`}
            icon={<IconClipboardCheck size={19} />}
            tone="primary"
            linkTo="/app/purchase-orders"
          />
        )}
        {canReadGr && (
          <KPICard
            label="GR draft (belum posted)"
            value={countByStatus(grs, 'DRAFT') ?? '—'}
            hint={`${grs?.length ?? 0} total GR`}
            icon={<IconTruck size={19} />}
            tone="success"
            linkTo="/app/goods-receipts"
          />
        )}
        {canReadBalance && (
          <KPICard
            label="SKU dengan stok"
            value={balances ? balances.filter((b) => b.qty_on_hand > 0).length : '—'}
            hint={`dari ${balances?.length ?? 0} baris saldo`}
            icon={<IconBox size={19} />}
            tone="success"
            linkTo="/app/stock-balance"
          />
        )}
      </div>

      {noAccess && <ErrorNote text="Role Anda belum memiliki izin baca untuk modul manapun di Vertical-Slice-1 — hubungi TENANT_OWNER." />}

      <div className="alex-grid-8-4" style={{ marginBottom: 'var(--alex-card-gap)' }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--alex-card-gap)' }}>
          {canReadPr && prs && prs.length > 0 && (
            <SectionCard title="Purchase Requests terbaru" action="/app/purchase-requests">
              <table style={tableStyle}>
                <thead>
                  <tr>
                    <th style={thStyle}>No. PR</th>
                    <th style={thStyle}>Status</th>
                    <th style={thStyle}>Kebutuhan</th>
                  </tr>
                </thead>
                <tbody>
                  {prs.slice(0, 5).map((pr) => (
                    <ClickableRow key={pr.pr_id} onClick={() => setSelectedPr(pr)}>
                      <td style={tdStyle}>{pr.pr_number}</td>
                      <td style={tdStyle}>
                        <StatusBadge status={pr.status} />
                      </td>
                      <td style={tdStyle}>{pr.needed_date ?? '—'}</td>
                    </ClickableRow>
                  ))}
                </tbody>
              </table>
            </SectionCard>
          )}

          {canReadPo && pos && pos.length > 0 && (
            <SectionCard title="Purchase Orders berjalan" action="/app/purchase-orders">
              <table style={tableStyle}>
                <thead>
                  <tr>
                    <th style={thStyle}>No. PO</th>
                    <th style={thStyle}>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {pos
                    .filter((po) => po.status === 'ISSUED' || po.status === 'PARTIALLY_RECEIVED')
                    .slice(0, 5)
                    .map((po) => (
                      <ClickableRow key={po.po_id} onClick={() => navigate('/app/purchase-orders')}>
                        <td style={tdStyle}>{po.po_number}</td>
                        <td style={tdStyle}>
                          <StatusBadge status={po.status} />
                        </td>
                      </ClickableRow>
                    ))}
                </tbody>
              </table>
            </SectionCard>
          )}

          {canReadLedger && recentMovements && recentMovements.length > 0 && (
            <SectionCard title="Pergerakan stok terbaru" action="/app/stock-ledger">
              <table style={tableStyle}>
                <thead>
                  <tr>
                    <th style={thStyle}>Waktu</th>
                    <th style={thStyle}>Tipe</th>
                    <th style={thStyle}>Qty</th>
                  </tr>
                </thead>
                <tbody>
                  {recentMovements.map((m) => (
                    <ClickableRow key={m.ledger_id} onClick={() => navigate(`/app/stock-ledger?item=${m.item_id}`)}>
                      <td style={tdStyle}>{new Date(m.occurred_at).toLocaleString('id-ID')}</td>
                      <td style={tdStyle}>{m.movement_type}</td>
                      <td style={{ ...tdStyle, color: m.qty_delta >= 0 ? 'var(--alex-success)' : 'var(--alex-danger)', fontWeight: 600 }}>
                        {m.qty_delta >= 0 ? '+' : ''}
                        {m.qty_delta}
                      </td>
                    </ClickableRow>
                  ))}
                </tbody>
              </table>
            </SectionCard>
          )}
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--alex-card-gap)' }}>
          {canReadLedger && ledgerTrend && (
            <SectionCard title="Tren pergerakan stok (14 hari)" action="/app/stock-ledger">
              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <IconCircle icon={<IconBox size={18} />} tone="primary" />
                <div style={{ fontSize: 'var(--alex-fs-caption)', color: 'var(--alex-text-muted)' }}>Net qty per hari, dari stock_ledger real</div>
              </div>
              <div style={{ marginTop: 10 }}>
                <Sparkline points={ledgerTrend} />
              </div>
            </SectionCard>
          )}

          {canReadBalance && balances && (
            <SectionCard title="Perlu Perhatian — Stok Habis">
              {balances.filter((b) => b.qty_on_hand <= 0).length === 0 ? (
                <p style={{ fontSize: 'var(--alex-fs-caption)', color: 'var(--alex-text-faint)', margin: 0 }}>Tidak ada SKU dengan stok habis.</p>
              ) : (
                <table style={tableStyle}>
                  <tbody>
                    {balances
                      .filter((b) => b.qty_on_hand <= 0)
                      .slice(0, 5)
                      .map((b) => {
                        const item = items?.find((it) => it.item_id === b.item_id);
                        return (
                          <ClickableRow key={`${b.warehouse_id}-${b.item_id}`} onClick={() => navigate('/app/stock-balance')}>
                            <td style={tdStyle}>{item?.name ?? b.item_id.slice(0, 8)}</td>
                            <td style={{ ...tdStyle, color: 'var(--alex-danger)', fontWeight: 600, textAlign: 'right' }}>{b.qty_on_hand}</td>
                          </ClickableRow>
                        );
                      })}
                  </tbody>
                </table>
              )}
            </SectionCard>
          )}

          {canReadSupplier && suppliers && suppliers.length > 0 && (
            <SectionCard title="Supplier" action="/app/suppliers">
              <table style={tableStyle}>
                <tbody>
                  {suppliers.slice(0, 5).map((s) => (
                    <ClickableRow key={s.supplier_id} onClick={() => navigate('/app/suppliers')}>
                      <td style={tdStyle}>{s.name}</td>
                      <td style={tdStyle}>
                        <StatusBadge status={s.status} />
                      </td>
                    </ClickableRow>
                  ))}
                </tbody>
              </table>
            </SectionCard>
          )}
        </div>
      </div>

      <PrDetailDrawer pr={selectedPr} open={!!selectedPr} onClose={() => setSelectedPr(null)} />
    </div>
  );
}
